"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _GLUtils = _interopRequireDefault(require("./GLUtils"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

class GLFilterPipeline {
  constructor(filters) {
    this.filters = filters;
    this._viewWidth = 0;
    this._viewHeight = 0;
    this.width = 0;
    this.height = 0;
    this.frameBuffers = [];
    this.frameBufferTextures = [];
  }

  bind(gl) {
    this._initializeIfNeeded(gl);
  }

  setViewSize(gl, width, height, offscreen = false) {
    if (this._viewWidth !== width || this._viewHeight !== height) {
      this._viewWidth = width;
      this._viewHeight = height;

      if (!offscreen && this.filters.length > 0) {
        this.filters[this.filters.length - 1].setViewSize(gl, width, height);
      }
    }
  }

  setSize(gl, width, height, offscreen = false) {
    if (width !== this.width || height !== this.height) {
      this.width = width;
      this.height = height;

      this._destroyTexturesAndFramesBuffers(gl);

      let prevWidth = width;
      let prevHeight = height;

      for (const filter of this.filters) {
        filter.setSize(gl, prevWidth, prevHeight);
        prevWidth = filter.outputTextureWidth;
        prevHeight = filter.outputTextureHeight;

        if (offscreen || filter !== this.filters[this.filters.length - 1]) {
          filter.setViewSize(gl, prevWidth, prevHeight);
        }
      }

      this._createTexturesAndFrameBuffers(gl);

      this.filters.forEach(filter => filter.setSize(gl, width, height));
    }
  }

  draw(gl, texture, frameBuffer, flipOverride = false) {
    if (this.frameBuffers.length === this.filters.length - 1) {
      var prevTexture = texture;

      for (var i = 0; i < this.filters.length; i++) {
        var isNotLast = i < this.filters.length - 1;

        if (frameBuffer && !isNotLast) {
          gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
        } else if (isNotLast) {
          gl.bindFramebuffer(gl.FRAMEBUFFER, this.frameBuffers[i]);
        } else {
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        }

        let flip = false;

        if (i === 0) {
          flip = !(this.filters.length % 2 === 0);
          flip = flipOverride ? !flip : flip;
        }

        this.filters[i].draw(gl, prevTexture, flip);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        if (isNotLast) {
          prevTexture = this.frameBufferTextures[i];
        }
      }
    }
  }

  process(imageURL, outputWidth = 0, outputHeight = 0, outputFileType = 'png') {
    const that = this;
    return new Promise(resolve => {
      const glCanvas = document.createElement('canvas');
      const gl = glCanvas.getContext('webgl');

      _GLUtils.default.loadTexture(gl, imageURL, (texture, textureWidth, textureHeight) => {
        const viewWidth = outputWidth !== 0 ? outputWidth : textureWidth;
        const viewHeight = outputHeight !== 0 ? outputHeight : textureHeight;
        glCanvas.width = viewWidth;
        glCanvas.height = viewHeight;
        const {
          pixels,
          width,
          height
        } = that.drawOffScreen(gl, texture, viewWidth, viewHeight, textureWidth, textureHeight);
        var canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        var context = canvas.getContext('2d');
        var imageData = context.createImageData(width, height);
        imageData.data.set(pixels);
        context.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/' + outputFileType));
      });
    });
  }

  drawOffScreen(gl, texture, viewWidth, viewHeight, textureWidth, textureHeight, flip = false) {
    this.bind(gl);
    this.setViewSize(gl, viewWidth, viewHeight);
    this.setSize(gl, textureWidth, textureHeight);

    this._createOffScreenTexturesAndFrameBuffers(gl, viewWidth, viewHeight);

    this.draw(gl, texture, this.offScreenFrameBuffer, flip);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.offScreenFrameBuffer);
    var pixels = new Uint8Array(viewWidth * viewHeight * 4);
    gl.readPixels(0, 0, viewWidth, viewHeight, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    this._destroyOffScreenTexturesAndFrameBuffers(gl);

    return {
      pixels,
      width: viewWidth,
      height: viewHeight
    };
  }

  _initializeIfNeeded(gl) {
    if (this.gl !== gl) {
      if (this.gl) {
        this._destroyTexturesAndFramesBuffers(this.gl);

        this.width = 0;
        this.height = 0;
        this._viewWidth = 0;
        this._viewHeight = 0;
      }

      this.gl = gl;
      this.filters.forEach(filter => filter.initialize(gl));
    }
  }

  _createTexturesAndFrameBuffers(gl) {
    this.frameBuffers = [];
    this.frameBufferTextures = [];

    if (this.filters.length > 1) {
      let prevWidth = this.filters[0].outputTextureWidth;
      let prevHeight = this.filters[0].outputTextureHeight;

      for (var i = 0; i < this.filters.length - 1; i++) {
        this.frameBuffers.push(gl.createFramebuffer());
        this.frameBufferTextures.push(gl.createTexture());

        this._createTextureAndFrameBuffer(gl, this.frameBufferTextures[i], this.frameBuffers[i], prevWidth, prevHeight);

        prevWidth = this.filters[i].outputTextureWidth;
        prevHeight = this.filters[i].outputTextureHeight;
      }
    }
  }

  _destroyTexturesAndFramesBuffers(gl) {
    this.frameBufferTextures.forEach(texture => gl.deleteTexture(texture));
    this.frameBuffers.forEach(frameBuffer => gl.deleteFramebuffer(frameBuffer));
    this.frameBuffers = [];
    this.frameBufferTextures = [];
  }

  _createOffScreenTexturesAndFrameBuffers(gl, width, height) {
    this.offScreenFrameBuffer = gl.createFramebuffer();
    this.offScreenFrameBuffferTexture = gl.createTexture();

    this._createTextureAndFrameBuffer(gl, this.offScreenFrameBuffferTexture, this.offScreenFrameBuffer, width, height);
  }

  _destroyOffScreenTexturesAndFrameBuffers(gl) {
    if (this.offScreenFrameBuffferTexture) {
      gl.deleteTexture(this.offScreenFrameBuffferTexture);
    }

    if (this.offScreenFrameBuffer) {
      gl.deleteFramebuffer(this.offScreenFrameBuffer);
    }
  }

  _createTextureAndFrameBuffer(gl, texture, frameBuffer, width, height) {
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameterf(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameterf(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameterf(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameterf(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

}

exports.default = GLFilterPipeline;